import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { localiApi } from '../lib/api';

const STORAGE_KEY = 'active_venue_id';

const VenueContext = createContext(null);

export const useVenue = () => {
  const ctx = useContext(VenueContext);
  if (!ctx) throw new Error('useVenue must be used within VenueProvider');
  return ctx;
};

export const VenueProvider = ({ children }) => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [availableVenues, setAvailableVenues] = useState([]); // [{ id, nome, slug, ruolo, tipo, ... }]
  const [activeVenue, setActiveVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pronto, setPronto] = useState(false);

  // Persistence layer: lo storage tiene SOLO l'id del locale attivo.
  // Ad ogni mount/login ricarichiamo l'elenco locali da /locali/me e validiamo l'id.
  const reload = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setActiveVenue(null);
      setAvailableVenues([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const venues = await localiApi.getMine();
      setAvailableVenues(venues);
      const storedId = localStorage.getItem(STORAGE_KEY);
      const found = storedId ? venues.find((v) => v.id === storedId) : null;
      if (found) {
        setActiveVenue(found);
      } else {
        // Se nello storage c'è un id non valido (es. locale disattivato o rimosso),
        // ripuliamo e lasciamo che il flusso di login decida.
        if (storedId) localStorage.removeItem(STORAGE_KEY);
        // Auto-select se l'utente ha esattamente un locale
        if (venues.length === 1) {
          localStorage.setItem(STORAGE_KEY, venues[0].id);
          setActiveVenue(venues[0]);
        } else {
          setActiveVenue(null);
        }
      }
    } catch (e) {
      setAvailableVenues([]);
      setActiveVenue(null);
    } finally {
      setLoading(false);
      setPronto(true);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    reload();
  }, [reload]);

  const selectVenue = (venueId) => {
    const v = availableVenues.find((x) => x.id === venueId);
    if (!v) return false;
    localStorage.setItem(STORAGE_KEY, venueId);
    setActiveVenue(v);
    return true;
  };

  const clearVenue = () => {
    localStorage.removeItem(STORAGE_KEY);
    setActiveVenue(null);
  };

  // Finché l'elenco locali non è stato caricato almeno una volta restiamo in "caricamento":
  // evita il redirect a /seleziona-locale al refresh della pagina con il locale già scelto.
  const inCaricamento = authLoading || loading || (isAuthenticated && !pronto);
  const needsSelection = isAuthenticated && !inCaricamento && availableVenues.length > 1 && !activeVenue;
  const noVenues = isAuthenticated && !inCaricamento && availableVenues.length === 0;

  return (
    <VenueContext.Provider
      value={{
        availableVenues,
        activeVenue,
        loading: inCaricamento,
        needsSelection,
        noVenues,
        selectVenue,
        clearVenue,
        reload,
      }}
    >
      {children}
    </VenueContext.Provider>
  );
};
